// Post-signup email marketing opt-in.
//
// This is what remains of the old AgeGate after the birthday screen was removed. The two
// questions lived in one modal but are unrelated: the age screen was a legal eligibility
// check, this is marketing consent, and it is still required in the countries listed in
// OPT_IN_REQUIRED (see WelcomeModal). Deleting the age flow must not silently take this with
// it — a consent prompt that stops appearing is not a UI regression, it is a compliance one.
import React, { useState } from "react";
import { apiService } from "../apiService";
import { useT } from "../i18n/useT";
import { useFocusTrap } from "../hooks/useFocusTrap";

const PRIVACY_URL = "https://www.iubenda.com/privacy-policy/52362461";
const TERMS_URL = "https://www.iubenda.com/terms-and-conditions/52362461";

const EmailOptInGate = () => {
  const t = useT();
  const trapRef = useFocusTrap(true);
  const [optIn, setOptIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Recorded either way. The field doubles as "have we asked?", so writing only on opt-IN
  // would re-prompt every session for anyone who declined.
  const submit = async () => {
    setSaving(true);
    try {
      await apiService.updateEmailOptIn(optIn);
    } catch {
      /* Swallowed on purpose — the close below is what keeps a failed write from trapping
         anyone, and there is nothing here the user could act on. */
    }
    setSaving(false);
    // CLOSE ON BOTH PATHS, and the failure path is the whole reason this exists.
    //
    // On success the userData snapshot listener in AuthContext sees email_marketing_opt_in
    // become non-null and unmounts this anyway, so the line is redundant there. On failure
    // nothing was written, that field stays null, and App's mount condition stays true — so
    // this modal (fixed inset-0, z-10000, focus-trapped, no close button) never goes away
    // and the only escape is a page reload. One network blip was a hard lockout for every
    // signed-in user in the 33 OPT_IN_REQUIRED countries.
    //
    // Dismissing locally costs a re-prompt next session, which is also the CORRECT compliance
    // outcome: we did not record consent, so we are obliged to ask again rather than treat
    // silence as an answer.
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="optin-title"
    >
      <div ref={trapRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-[420px] mx-4 p-8">
        <p id="optin-title" className="text-xl font-semibold text-gray-900 mb-1.5">
          {t("welcome_title")}
        </p>

        <label className="flex items-center gap-2.5 cursor-pointer my-5">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-[#635BFF] focus:ring-[#635BFF] cursor-pointer flex-shrink-0"
          />
          <span className="text-sm text-gray-500">
            {t("auth_email_optin")}{" "}
            <span className="text-[#635BFF] font-medium">{t("welcome_optin_reward")}</span>
          </span>
        </label>

        <p className="text-xs leading-relaxed text-gray-400 mb-4">
          {t("auth_agree_prefix")}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-[#635BFF]">
            {t("auth_terms")}
          </a>
          {t("auth_and")}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-[#635BFF]">
            {t("auth_privacy")}
          </a>.
        </p>

        <button
          onClick={submit}
          disabled={saving}
          className="w-full py-3 bg-[#635BFF] text-white rounded-xl text-[15px] font-semibold hover:bg-[#4B44CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t("age_gate_saving") : t("age_gate_continue")}
        </button>
      </div>
    </div>
  );
};

export default EmailOptInGate;
